import type { Zone } from "../types";
import { API_ENDPOINTS } from "./apiEndpoints";
import { getFeatureTypeName } from "./timetablePeriods";

// Build the XML body for the WFS GetFeature request
async function buildWfsCityZonesRequestXml(): Promise<string> {
  const payZonesLayer = await getFeatureTypeName("PayZones");

  return `
<GetFeature
  service="WFS"
  version="1.0.0"
  outputFormat="application/json"
  xmlns:wfs="http://www.opengis.net/wfs"
  xmlns:ogc="http://www.opengis.net/ogc"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xsi:schemaLocation="http://www.opengis.net/wfs 
                      http://schemas.opengis.net/wfs/1.0.0/WFS-basic.xsd">
  <Query typeName="${payZonesLayer}">
    <wfs:PropertyName>namn</wfs:PropertyName>
    <wfs:PropertyName>geom</wfs:PropertyName> 
  </Query>
</GetFeature>
  `.trim();
}
// In-memory cache for city zones
let _cityZonesCache: Zone[] | null = null;

// IDs of zones to exclude from results
const EXCLUDED_IDS = ["nyazoner_0", "nyazoner_1"];

// Fetch all city zones from the API
export async function fetchAllCityZones(): Promise<Zone[]> {
  if (_cityZonesCache) return _cityZonesCache;

  const xml = await buildWfsCityZonesRequestXml();
  const body = `=${encodeURIComponent(xml)}`;

  const res = await fetch(API_ENDPOINTS.GEO_FETCH, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
    },
    body,
  });

  if (!res.ok) {
    const text = await res.text();
    console.error("Geo/fetch (nyazoner) fel:", res.status, text);
    throw new Error("Geo/fetch (nyazoner) fel: " + res.status);
  }

  const geoJson = await res.json();
  const features: any[] = geoJson.features || [];

  // Map GeoJSON features to Zone objects
  const zones = features.map((f, idx) => {
    const coords: { lat: number; lng: number }[] = [];
    const geom = f.geometry;
    if (geom.type === "Polygon") {
      coords.push(
        ...geom.coordinates[0].map(([lng, lat]: [number, number]) => ({
          lat,
          lng,
        }))
      );
    } else if (geom.type === "MultiPolygon") {
      coords.push(
        ...geom.coordinates[0][0].map(([lng, lat]: [number, number]) => ({
          lat,
          lng,
        }))
      );
    }
    // Return the Zone object
    return {
      id: f.properties.id ?? `nyazoner_${idx}`,
      name: f.properties.namn,
      polygon: coords,
    };
  });
  // Filter out excluded zones
  const filteredZones = zones.filter((z) => !EXCLUDED_IDS.includes(z.id));
  _cityZonesCache = filteredZones;
  return filteredZones;
}

// Fetch a single city zone by its ID
export async function fetchCityZoneById(id: string): Promise<Zone | null> {
  if (EXCLUDED_IDS.includes(id)) return null;
  const all = await fetchAllCityZones();
  return all.find((z) => z.id === id) ?? null;
}
